/**
 * A Timely allocation for collections: a person's decision about which bills
 * an unallocated receipt is *for*, for the collection work only
 * (docs/tally/17 §5–§6).
 *
 * ## What it is
 *
 * The accountant left ₹700 On Account; the customer says it was for INV-001
 * and INV-002. A collector who knows that should not keep chasing INV-001.
 * So a person with the right may record, in Timely: "for collections, treat
 * ₹500 of it as against INV-001 and ₹200 against INV-002", with a reason.
 *
 * ## What it can never be
 *
 *   - It never changes TallyPrime, a balance, a bill, a date or a voucher.
 *   - It is never called "allocated in TallyPrime".
 *   - It can only re-present credit TallyPrime itself holds against no bill
 *     (on account, an advance, a credit note entered as a new reference).
 *     It cannot invent money: the account's net is the same before and after.
 *   - It cannot sit over an allocation TallyPrime states: an exact allocation
 *     outranks it, always.
 *
 * ## When TallyPrime later speaks
 *
 * Nothing is overwritten. At every update the allocations are checked again
 * (`reconcileOperationalAllocations`):
 *
 *   - the receipt is now allocated in TallyPrime → **superseded**, kept with
 *     both versions side by side ("TallyPrime: ₹700 → INV-002; Timely had:
 *     ₹500 → INV-001, ₹200 → INV-002") and whether they agree;
 *   - TallyPrime no longer holds enough unallocated credit on the account to
 *     cover the Timely allocations → every active one on the account is
 *     **suspended** for a person to review. Timely does not choose which
 *     survives;
 *   - a bill it names is no longer open, or holds less than allocated →
 *     **suspended**, the same way.
 *
 * A suspended allocation is never re-activated by itself. The current
 * accounting view always uses TallyPrime's facts; notes, promises and history
 * are never touched by any of this.
 */
import type { AllocationLine, CreditEvent } from './allocationConfidence.js';
import type { AccountPosition } from './receivablesPosition.js';
import { fromPaise, toPaise } from './receivablesFacts.js';

export type OperationalState = 'active' | 'suspended' | 'superseded' | 'withdrawn';
export type OperationalStateReason =
    | 'allocated_in_tally'
    | 'credit_no_longer_unallocated'
    | 'bill_changed_in_tally'
    | 'withdrawn_by_person';

export const OPERATIONAL_STATE_WORDS: Record<OperationalStateReason, string> = {
    allocated_in_tally: 'TallyPrime now allocates this receipt. TallyPrime’s allocation is used; the Timely allocation is kept for the record.',
    credit_no_longer_unallocated: 'TallyPrime no longer holds enough unallocated credit on this account to cover the Timely allocations. Review them.',
    bill_changed_in_tally: 'A bill this allocation names has changed in TallyPrime. Review it.',
    withdrawn_by_person: 'Withdrawn.',
};

export interface OperationalAllocationLine {
    billKey: string;
    ref: string;
    /** Positive. */
    amount: number;
}

export interface OperationalAllocation {
    id: string;
    sourceKey: string;
    /** The credit it re-presents, when there is one event for it; null: the account's unallocated credit in general. */
    creditEventId: string | null;
    lines: OperationalAllocationLine[];
    reason: string;
    createdBy: string;
    createdAt: string;
    state: OperationalState;
    stateReason?: OperationalStateReason;
    /** When superseded by TallyPrime: what TallyPrime says, beside what Timely had. */
    comparison?: { tally: AllocationLine[]; sameAsTimely: boolean };
}

export interface OperationalDraft {
    sourceKey: string;
    creditEventId: string | null;
    lines: { billKey: string; amount: number }[];
    reason: string;
}

export interface OperationalProblem { code: string; message: string }

/**
 * The credit TallyPrime holds on an account against no bill: credit-side
 * bills (advances, credit notes as new references) and money received On
 * Account. Only this may be re-presented by a Timely allocation.
 */
export function unallocatedCreditPool(account: Pick<AccountPosition, 'creditBills' | 'unbilled' | 'billWise'>): number {
    if (!account.billWise) return 0;
    return fromPaise(toPaise(account.creditBills) + Math.max(0, -toPaise(account.unbilled)));
}

const activeOn = (all: readonly OperationalAllocation[], sourceKey: string, except?: string) =>
    all.filter(a => a.sourceKey === sourceKey && a.state === 'active' && a.id !== except);

/** Every reason a draft cannot be saved, in words a collector can act on. Nothing is trimmed or rounded to fit. */
export function validateOperationalAllocation(
    draft: OperationalDraft,
    ctx: { account: AccountPosition | undefined; existing: readonly OperationalAllocation[]; event?: CreditEvent },
): OperationalProblem[] {
    const problems: OperationalProblem[] = [];
    const { account } = ctx;
    if (!account || account.sourceKey !== draft.sourceKey) return [{ code: 'no_account', message: 'That account is not in the latest update from TallyPrime.' }];
    if (!account.billWise) problems.push({ code: 'not_bill_wise', message: 'TallyPrime keeps no bills for this ledger, so there is nothing to allocate to.' });
    if (draft.reason.trim().length < 3) problems.push({ code: 'reason', message: 'Say why — for example, what the customer told you.' });
    if (draft.reason.length > 500) problems.push({ code: 'reason', message: 'Keep the reason under 500 characters.' });
    if (draft.lines.length === 0) problems.push({ code: 'no_lines', message: 'Choose at least one bill.' });
    if (draft.lines.length > 50) problems.push({ code: 'too_many', message: 'At most 50 bills in one allocation.' });

    if (ctx.event) {
        if (ctx.event.sourceKey !== draft.sourceKey) problems.push({ code: 'wrong_account', message: 'That receipt is on another account.' });
        if (ctx.event.status === 'EXACT_FROM_TALLY' && ctx.event.unallocated.length === 0) problems.push({ code: 'exact', message: 'TallyPrime already allocates this receipt. Its allocation is the one used.' });
        if (ctx.event.status === 'DERIVED_WITH_HIGH_CONFIDENCE' && ctx.event.unallocated.length === 0) problems.push({ code: 'derived', message: 'TallyPrime’s bills already show where this went.' });
    }

    const others = activeOn(ctx.existing, draft.sourceKey);
    const onBill = new Map<string, number>();
    for (const o of others) for (const l of o.lines) onBill.set(l.billKey, (onBill.get(l.billKey) ?? 0) + toPaise(l.amount));
    const seen = new Set<string>();
    let totalP = 0;
    for (const line of draft.lines) {
        const p = toPaise(line.amount);
        const bill = account.bills.find(b => b.key === line.billKey);
        if (seen.has(line.billKey)) problems.push({ code: 'duplicate', message: `${bill?.ref ?? 'A bill'} is chosen twice.` });
        seen.add(line.billKey);
        if (!(p > 0) || Math.abs(line.amount * 100 - p) > 1e-6) { problems.push({ code: 'amount', message: 'Each amount must be above zero, in rupees and paise.' }); continue; }
        if (!bill || bill.side !== 'Dr' || toPaise(bill.pending) <= 0) { problems.push({ code: 'not_open', message: 'Only an open bill the customer owes can be chosen.' }); continue; }
        const room = toPaise(bill.pending) - (onBill.get(line.billKey) ?? 0);
        if (p > room) problems.push({ code: 'over_bill', message: `${bill.ref} has ${fromPaise(Math.max(0, room)).toFixed(2)} left to allocate.` });
        totalP += p;
    }
    const poolP = toPaise(unallocatedCreditPool(account)) - others.reduce((s, o) => s + o.lines.reduce((t, l) => t + toPaise(l.amount), 0), 0);
    if (totalP > poolP) problems.push({ code: 'over_credit', message: `TallyPrime holds ${fromPaise(Math.max(0, poolP)).toFixed(2)} on this account against no bill. A Timely allocation cannot be more than that.` });
    if (ctx.event && ctx.event.status !== 'AMBIGUOUS') {
        const eventUnallocatedP = ctx.event.unallocated.reduce((s, u) => s + toPaise(u.amount), 0);
        if (totalP > eventUnallocatedP) problems.push({ code: 'over_event', message: `This receipt has ${fromPaise(eventUnallocatedP).toFixed(2)} unallocated in TallyPrime.` });
    }
    return problems;
}

export interface CollectionBill {
    key: string;
    ref: string;
    /** TallyPrime's pending, unchanged. */
    pending: number;
    /** What active Timely allocations treat as covered, for collections only. */
    coveredForCollections: number;
    /** What is left to chase. */
    toCollect: number;
}

export interface CollectionView {
    sourceKey: string;
    bills: CollectionBill[];
    /** Unallocated credit Timely allocations have used. */
    allocatedByTimely: number;
    /** Unallocated credit still against no bill, after Timely allocations. */
    unallocatedRemaining: number;
    /** The account's balance: identical to TallyPrime's, always. */
    closing: number;
}

/**
 * The account as a collector works it: TallyPrime's bills, less what active
 * Timely allocations cover. The balance is unchanged — the credit was already
 * in it; only where it is shown moves.
 */
export function collectionView(account: AccountPosition, allocations: readonly OperationalAllocation[]): CollectionView {
    const active = activeOn(allocations, account.sourceKey);
    const covered = new Map<string, number>();
    for (const a of active) for (const l of a.lines) covered.set(l.billKey, (covered.get(l.billKey) ?? 0) + toPaise(l.amount));
    const bills = account.bills.filter(b => b.side === 'Dr').map(b => {
        const c = Math.min(covered.get(b.key) ?? 0, toPaise(b.pending));
        return { key: b.key, ref: b.ref, pending: b.pending, coveredForCollections: fromPaise(c), toCollect: fromPaise(toPaise(b.pending) - c) };
    });
    const usedP = bills.reduce((s, b) => s + toPaise(b.coveredForCollections), 0);
    return {
        sourceKey: account.sourceKey,
        bills,
        allocatedByTimely: fromPaise(usedP),
        unallocatedRemaining: fromPaise(toPaise(unallocatedCreditPool(account)) - usedP),
        closing: account.closing,
    };
}

export interface OperationalTransition {
    id: string;
    to: Exclude<OperationalState, 'active' | 'withdrawn'>;
    reason: OperationalStateReason;
    comparison?: { tally: AllocationLine[]; sameAsTimely: boolean };
}

const sameLines = (tally: readonly AllocationLine[], timely: readonly OperationalAllocationLine[]) => {
    const k = (ref: string, p: number) => `${ref.trim().toLowerCase()}|${p}`;
    const a = tally.map(l => k(l.ref, toPaise(l.amount))).sort().join(';');
    const b = timely.map(l => k(l.ref, toPaise(l.amount))).sort().join(';');
    return a === b;
};

/**
 * What the latest facts mean for each active Timely allocation. Returns only
 * the changes; the caller records them, with the time and the update that
 * caused them. Nothing is deleted and nothing is re-activated.
 *
 * `supersededBy` maps an earlier credit event's id to the event TallyPrime's
 * later read gave the same voucher (an altered voucher keeps its GUID).
 */
export function reconcileOperationalAllocations(input: {
    allocations: readonly OperationalAllocation[];
    accounts: ReadonlyMap<string, AccountPosition>;
    supersededBy?: ReadonlyMap<string, CreditEvent>;
}): OperationalTransition[] {
    const out: OperationalTransition[] = [];
    const decided = new Set<string>();
    const active = input.allocations.filter(a => a.state === 'active');

    // 1. The receipt is now allocated in TallyPrime.
    for (const a of active) {
        if (!a.creditEventId) continue;
        const later = input.supersededBy?.get(a.creditEventId);
        if (later && later.status === 'EXACT_FROM_TALLY' && later.allocations.length > 0) {
            out.push({ id: a.id, to: 'superseded', reason: 'allocated_in_tally', comparison: { tally: later.allocations, sameAsTimely: sameLines(later.allocations, a.lines) } });
            decided.add(a.id);
        }
    }

    // 2. Per account: enough unallocated credit left, and every bill still open with room.
    const byAccount = new Map<string, OperationalAllocation[]>();
    for (const a of active) if (!decided.has(a.id)) byAccount.set(a.sourceKey, [...(byAccount.get(a.sourceKey) ?? []), a]);
    for (const [sourceKey, list] of byAccount) {
        const account = input.accounts.get(sourceKey);
        const totalP = list.reduce((s, a) => s + a.lines.reduce((t, l) => t + toPaise(l.amount), 0), 0);
        if (!account || totalP > toPaise(unallocatedCreditPool(account))) {
            for (const a of list) out.push({ id: a.id, to: 'suspended', reason: 'credit_no_longer_unallocated' });
            continue;
        }
        const onBill = new Map<string, number>();
        for (const a of list) for (const l of a.lines) onBill.set(l.billKey, (onBill.get(l.billKey) ?? 0) + toPaise(l.amount));
        const broken = new Set<string>();
        for (const [billKey, p] of onBill) {
            const bill = account.bills.find(b => b.key === billKey);
            if (!bill || bill.side !== 'Dr' || toPaise(bill.pending) < p) broken.add(billKey);
        }
        for (const a of list) if (a.lines.some(l => broken.has(l.billKey))) out.push({ id: a.id, to: 'suspended', reason: 'bill_changed_in_tally' });
    }
    return out;
}
